/**
 * OriginHealthDot — live green/amber dot for the Bulk Photo Sorter toolbar.
 *
 * Pings `/api/health/upload` every 30s. Also observes the last upload result
 * so a fresh failure surfaces immediately instead of waiting for the next
 * poll (users would otherwise trust a stale green dot for up to 30s).
 *
 * Colour semantics:
 *   green  — last probe OK, no queued variant jobs
 *   amber  — probe OK but variant queue is non-zero (large uploads in flight)
 *   red    — probe failed OR consumer reported a recent /upload failure
 *   grey   — probing…
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Circle } from "lucide-react";

const POLL_MS = 30000;
// If the caller reports a failed upload, we downgrade the dot to red for this
// long before returning to the polled colour. Keeps the signal honest without
// getting stuck on red after a transient blip.
const FAILURE_STICKY_MS = 60000;

export function OriginHealthDot({ lastFailureAt }) {
  const [state, setState] = useState("probing"); // probing | green | amber | red
  const [queued, setQueued] = useState(null);
  const [latency, setLatency] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  // Under React 19 StrictMode, the mount effect's cleanup fires immediately
  // during the first double-invoke of the component. If we only *set*
  // mountedRef=false on cleanup, it stays false forever and every setState
  // in `probe()` becomes a silent no-op — the badge is stuck on "probing".
  // Setting it true on mount as well makes the pattern StrictMode-safe.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const probe = async () => {
    const t0 = performance.now();
    try {
      const { data } = await api.get("/health/upload");
      const ms = Math.round(performance.now() - t0);
      if (!mountedRef.current) return;
      setLatency(ms);
      setQueued(data?.variant_queue ?? null);
      // Recent-failure sticky: even a green probe stays amber if a caller
      // reported a failed /upload in the last minute.
      const recentFail = lastFailureAt && Date.now() - lastFailureAt < FAILURE_STICKY_MS;
      if (recentFail) setState("red");
      else if (data?.variant_queue && data.variant_queue > 0) setState("amber");
      else if (ms > 2000) setState("amber");
      else setState("green");
    } catch {
      if (!mountedRef.current) return;
      setState("red");
      setLatency(null);
    }
  };

  useEffect(() => {
    probe();
    timerRef.current = setInterval(probe, POLL_MS);
    return () => clearInterval(timerRef.current);
    // Re-run probe when caller signals a fresh failure so the badge updates
    // immediately without waiting on the next scheduled poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastFailureAt]);

  const color = {
    probing: "text-taupe fill-taupe/40",
    green: "text-emerald-700 fill-emerald-600",
    amber: "text-amber-700 fill-amber-500",
    red: "text-red-700 fill-red-600",
  }[state];

  const label = {
    probing: "checking…",
    green: "origin ok",
    amber: `origin busy${queued ? ` · ${queued}` : ""}`,
    red: "origin down",
  }[state];

  // Longer text goes into the tooltip so the toolbar row never wraps.
  const tip = {
    probing: "checking origin health…",
    green: `origin healthy${latency != null ? ` · ${latency}ms` : ""}`,
    amber: `origin busy${queued ? ` · ${queued} queued` : ""}${latency != null ? ` · ${latency}ms` : ""}`,
    red: "origin unreachable — the next attach may fail",
  }[state];

  return (
    <span
      className="inline-flex items-center gap-1.5 text-[0.6rem] tracking-widest uppercase text-taupe whitespace-nowrap"
      title={tip}
      data-testid="bp-origin-health"
      data-state={state}
    >
      <Circle className={`w-2.5 h-2.5 ${color}`} strokeWidth={0} />
      {label}
    </span>
  );
}
